import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminService from '../../service/AdminService';
import { Card, Spinner, Alert, Button, Badge, Form } from 'react-bootstrap';
import { Building2, Users, ChevronDown, ChevronUp, Eye, AlertCircle } from 'lucide-react';

export default function SucursalesUsuarios() {
  const navigate = useNavigate();
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSucursal, setExpandedSucursal] = useState(null);

  // Estado de paginación por sucursal
  const [pagination, setPagination] = useState({});  
  // { sucursalId: { page: 1, pageSize: 10 } }

  useEffect(() => {
    const fetchSucursales = async () => {
      try {
        setError('');
        const data = await AdminService.getSucursalesConUsuarios();

        // Inicializa paginación para cada sucursal
        const initialPagination = {};
        data.forEach(s => {
          initialPagination[s.id] = { page: 1, pageSize: 10 };
        });
        setPagination(initialPagination);

        setSucursales(data);
      } catch (error) {
        console.error('Error al obtener sucursales con usuarios:', error);

        if (error.response?.status === 401) {
          setError('No autenticado. Por favor, inicia sesión nuevamente.');
        } else if (error.response?.status === 403) {
          setError('No tienes permisos de administrador para acceder a esta información.');
        } else {
          setError('Error al cargar las sucursales y usuarios. Por favor, intenta nuevamente.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSucursales();
  }, []);

  const toggleSucursal = (sucursalId) => {
    setExpandedSucursal(expandedSucursal === sucursalId ? null : sucursalId);
  };

  const handleVerDetalles = (usuarioId) => {
    navigate(`/home-admin/user-details/${usuarioId}`);
  };

  const setPage = (sucursalId, newPage) => {
    setPagination(prev => ({
      ...prev,
      [sucursalId]: {
        ...prev[sucursalId],
        page: newPage
      }
    }));
  };

  const setPageSize = (sucursalId, newSize) => {
    setPagination(prev => ({
      ...prev,
      [sucursalId]: {
        page: 1,
        pageSize: newSize
      }
    }));
  };

  const getPagedUsers = (usuarios, sucursalId) => {
    const { page, pageSize } = pagination[sucursalId];
    const start = (page - 1) * pageSize;
    return usuarios.slice(start, start + pageSize);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h2 className="mb-1">Sucursales y Usuarios</h2>
          <p className="text-muted mb-0">Gestiona y visualiza usuarios por sucursal</p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
          <AlertCircle size={18} className="me-2" />
          {error}
        </Alert>
      )}

      {sucursales.length === 0 && !loading && (
        <Alert variant="info" className="mb-4">
          No hay sucursales disponibles.
        </Alert>
      )}

      <div className="row g-3">
        {sucursales.map((sucursal) => {
          const pag = pagination[sucursal.id];
          const totalUsers = sucursal.usuarios?.length || 0;

          const pagedUsers = pag 
            ? getPagedUsers(sucursal.usuarios || [], sucursal.id) 
            : [];

          const totalPages = Math.ceil(totalUsers / pag.pageSize);

          return (
            <div key={sucursal.id} className="col-12">
              <Card className="border-0 shadow-sm">

                <Card.Header
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: 'var(--card-bg)',
                    borderBottom: '1px solid var(--border-color)'
                  }}
                  onClick={() => toggleSucursal(sucursal.id)}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div className="bg-primary bg-opacity-10 p-2 rounded">
                      <Building2 className="text-primary" size={24} />
                    </div>
                    <div>
                      <h5 className="mb-0">{sucursal.nombre}</h5>
                      {sucursal.ubicacion && (
                        <small className="text-muted">{sucursal.ubicacion}</small>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <Badge bg="secondary" className="px-3 py-2">
                      <Users size={16} className="me-1" />
                      {totalUsers} usuario{totalUsers !== 1 ? 's' : ''}
                    </Badge>
                    {expandedSucursal === sucursal.id ? (
                      <ChevronUp size={20} className="text-muted" />
                    ) : (
                      <ChevronDown size={20} className="text-muted" />
                    )}
                  </div>
                </Card.Header>

                {expandedSucursal === sucursal.id && (
                  <Card.Body style={{ backgroundColor: 'var(--card-bg)' }}>

                    {/* Controles de paginación */}
                    {totalUsers > 0 && (
                      <div className="mb-3 d-flex justify-content-between align-items-center">

                        <Form.Select
                          style={{ width: '120px' }}
                          value={pag.pageSize}
                          onChange={(e) => setPageSize(sucursal.id, Number(e.target.value))}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={15}>15</option>
                          <option value={20}>20</option>
                        </Form.Select>

                        <div className="d-flex align-items-center gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={pag.page === 1}
                            onClick={() => setPage(sucursal.id, pag.page - 1)}
                          >
                            Anterior
                          </Button>

                          <span>{pag.page} / {totalPages}</span>

                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={pag.page === totalPages}
                            onClick={() => setPage(sucursal.id, pag.page + 1)}
                          >
                            Siguiente
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Lista paginada */}
                    {pagedUsers.length > 0 ? (
                      <div className="row g-3">
                        {pagedUsers.map((usuario) => (
                          <div key={usuario.id} className="col-12 col-md-6 col-lg-4">
                            <Card className="border shadow-sm h-100">
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <div>
                                    <h6 className="mb-1">
                                      {usuario.nombre} {usuario.apellido}
                                    </h6>
                                    <small className="text-muted d-block">
                                      Cédula: {usuario.cedula}
                                    </small>
                                  </div>
                                  <div>
                                    {usuario.isAdmin && (
                                      <Badge bg="warning" className="mb-2 d-block">
                                        Admin
                                      </Badge>
                                    )}
                                    {usuario.isActive ? (
                                      <Badge bg="success" className="d-block">
                                        Activo
                                      </Badge>
                                    ) : (
                                      <Badge bg="danger" className="d-block">
                                        Inactivo
                                      </Badge>
                                    )}
                                  </div>
                                </div>

                                <Button
                                  variant="primary"
                                  size="sm"
                                  className="w-100"
                                  onClick={() => handleVerDetalles(usuario.id)}
                                >
                                  <Eye size={16} className="me-2" />
                                  Ver más detalles
                                </Button>
                              </Card.Body>
                            </Card>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Alert variant="info" className="mb-0">
                        Esta sucursal no tiene usuarios asignados.
                      </Alert>
                    )}

                  </Card.Body>
                )}
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}